/* Animated completion button with multiple satisfying animation variants.
   Used to mark feed items as complete with delightful micro-interactions. */

import React, { useState, useCallback, useRef, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";

export type CompletionVariant =
  | "burst" | "pop" | "ripple" | "snap"
  | "confetti" | "confetti-clean"
  | "twitter-ring" | "twitter-burst" | "twitter-sparkle"
  | "ink" | "press" | "swing" | "wipe";

interface CompletionAnimationProps {
  variant: CompletionVariant;
  onComplete?: () => void;
  label?: string;
  size?: "sm" | "md";
}

// ─── Particle helpers ───────────────────────────────────────────────

interface Particle {
  id: number;
  x: number;
  y: number;
  scale: number;
  angle: number;
  distance: number;
}

/* Particle colors derived from CSS --primary at varying opacities.
   We use oklch with the same hue/chroma as the theme primary. */
const PARTICLE_OPACITIES = [1, 0.85, 0.7, 0.55, 0.9, 0.75];

function createBurstParticles(count: number): Particle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 0,
    y: 0,
    scale: 0.4 + Math.random() * 0.6,
    angle: (360 / count) * i + (Math.random() - 0.5) * 20,
    distance: 20 + Math.random() * 18,
  }));
}

function createConfettiParticles(count: number): Particle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: (Math.random() - 0.5) * 40,
    y: 0,
    scale: 0.3 + Math.random() * 0.5,
    angle: -90 + (Math.random() - 0.5) * 60,
    distance: 30 + Math.random() * 30,
  }));
}

// ─── Variant: Burst (Twitter heart-style) ───────────────────────────

function BurstAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  const particles = useRef(createBurstParticles(12));

  return (
    <div className="relative flex items-center justify-center">
      {/* Ring burst */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            className="absolute inset-0 rounded-full"
            initial={{ scale: 0, opacity: 1 }}
            animate={{ scale: 2.2, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{
              border: "2px solid var(--primary)",
            }}
          />
        )}
      </AnimatePresence>

      {/* Particles */}
      <AnimatePresence>
        {isCompleted &&
          particles.current.map((p) => {
            const rad = (p.angle * Math.PI) / 180;
            const tx = Math.cos(rad) * p.distance;
            const ty = Math.sin(rad) * p.distance;
            return (
              <motion.div
                key={p.id}
                className="absolute rounded-full bg-primary"
                style={{
                  width: 5 * p.scale,
                  height: 5 * p.scale,
                  opacity: PARTICLE_OPACITIES[p.id % PARTICLE_OPACITIES.length],
                }}
                initial={{ x: 0, y: 0, scale: 0 }}
                animate={{
                  x: tx,
                  y: ty,
                  scale: [0, 1.2, 0],
                  opacity: [PARTICLE_OPACITIES[p.id % PARTICLE_OPACITIES.length], PARTICLE_OPACITIES[p.id % PARTICLE_OPACITIES.length], 0],
                }}
                transition={{
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1],
                  delay: p.id * 0.015,
                }}
              />
            );
          })}
      </AnimatePresence>

      {/* Check icon */}
      <motion.div
        animate={
          isCompleted
            ? {
                scale: [1, 1.5, 0.85, 1.1, 1],
                rotate: [0, -10, 5, 0],
              }
            : { scale: 1, rotate: 0 }
        }
        transition={
          isCompleted
            ? {
                duration: 0.5,
                ease: [0.22, 1, 0.36, 1],
                times: [0, 0.3, 0.55, 0.75, 1],
              }
            : { duration: 0.15 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className={`size-5 transition-colors duration-200 ${isCompleted ? "text-primary" : ""}`}
          style={{ strokeWidth: isCompleted ? 3 : 2 }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Pop (clean scale punch + glow, no particles) ──────────

function PopAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  return (
    <div className="relative flex items-center justify-center">
      {/* Soft glow that flashes and fades */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            className="absolute rounded-full bg-primary/30"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: [0.5, 1.8, 1.4], opacity: [0, 0.6, 0] }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ width: 28, height: 28 }}
          />
        )}
      </AnimatePresence>

      {/* Check icon — quick punch scale */}
      <motion.div
        animate={
          isCompleted
            ? { scale: [1, 1.8, 0.85, 1.05, 1] }
            : { scale: 1 }
        }
        transition={
          isCompleted
            ? {
                duration: 0.45,
                ease: [0.22, 1, 0.36, 1],
                times: [0, 0.25, 0.5, 0.75, 1],
              }
            : { duration: 0.1 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className={`size-5 transition-colors duration-150 ${isCompleted ? "text-primary" : ""}`}
          style={{ strokeWidth: isCompleted ? 3 : 2 }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Ripple (YouTube thumbs up-style) ─────────────────────

function RippleAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  return (
    <div className="relative flex items-center justify-center">
      {/* Ripple rings */}
      <AnimatePresence>
        {isCompleted && (
          <>
            <motion.div
              className="absolute rounded-full bg-primary/20"
              initial={{ width: 0, height: 0, opacity: 0.6 }}
              animate={{ width: 56, height: 56, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              style={{ x: "-50%", y: "-50%", left: "50%", top: "50%" }}
            />
            <motion.div
              className="absolute rounded-full bg-primary/15"
              initial={{ width: 0, height: 0, opacity: 0.4 }}
              animate={{ width: 72, height: 72, opacity: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: 0.08 }}
              style={{ x: "-50%", y: "-50%", left: "50%", top: "50%" }}
            />
          </>
        )}
      </AnimatePresence>

      {/* Fill circle behind icon */}
      <motion.div
        className="absolute rounded-full bg-primary"
        animate={
          isCompleted
            ? { scale: [0, 1.15, 1], opacity: [0, 1, 1] }
            : { scale: 0, opacity: 0 }
        }
        transition={{
          duration: 0.35,
          ease: [0.34, 1.56, 0.64, 1],
        }}
        style={{ width: 30, height: 30 }}
      />

      {/* Check icon */}
      <motion.div
        className="relative z-10"
        animate={
          isCompleted
            ? { scale: [1, 0.7, 1.2, 1], y: [0, 2, -3, 0] }
            : { scale: 1, y: 0 }
        }
        transition={
          isCompleted
            ? {
                duration: 0.45,
                ease: [0.22, 1, 0.36, 1],
                times: [0, 0.25, 0.6, 1],
              }
            : { duration: 0.15 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className="size-5 transition-colors duration-150"
          style={{
            color: isCompleted ? "white" : "currentColor",
            strokeWidth: isCompleted ? 2.5 : 2,
          }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Snap (clean fill + bounce, no splash) ─────────────────

function SnapAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  return (
    <div className="relative flex items-center justify-center">
      {/* Background pill that snaps in */}
      <motion.div
        className="absolute rounded-md bg-primary"
        animate={
          isCompleted
            ? { scaleX: [0, 1.1, 1], scaleY: [0.6, 1.05, 1], opacity: [0, 1, 1] }
            : { scaleX: 0, scaleY: 0, opacity: 0 }
        }
        transition={
          isCompleted
            ? { duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }
            : { duration: 0.1 }
        }
        style={{ width: 28, height: 28, borderRadius: 7 }}
      />

      {/* Check icon — squishes down then pops up */}
      <motion.div
        className="relative z-10"
        animate={
          isCompleted
            ? {
                scale: [1, 0.6, 1.15, 0.95, 1],
                rotate: [0, -6, 4, 0],
              }
            : { scale: 1, rotate: 0 }
        }
        transition={
          isCompleted
            ? {
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1],
                times: [0, 0.2, 0.5, 0.75, 1],
              }
            : { duration: 0.1 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className="size-5 transition-colors duration-150"
          style={{
            color: isCompleted ? "var(--primary-foreground)" : "currentColor",
            strokeWidth: isCompleted ? 2.5 : 2,
          }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Confetti (Medium clap-style) ──────────────────────────

function ConfettiAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  const particles = useRef(createConfettiParticles(16));

  return (
    <div className="relative flex items-center justify-center">
      {/* Floating "+1" text */}
      <AnimatePresence>
        {isCompleted && (
          <motion.span
            className="absolute text-xs font-bold text-primary pointer-events-none select-none"
            initial={{ y: 0, opacity: 1, scale: 0.5 }}
            animate={{ y: -28, opacity: 0, scale: 1.1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            Done!
          </motion.span>
        )}
      </AnimatePresence>

      {/* Confetti particles */}
      <AnimatePresence>
        {isCompleted &&
          particles.current.map((p) => {
            const rad = (p.angle * Math.PI) / 180;
            const tx = p.x + Math.cos(rad) * p.distance;
            const ty = Math.sin(rad) * p.distance;
            const isRect = p.id % 3 === 0;
            return (
              <motion.div
                key={p.id}
                className="absolute bg-primary"
                style={{
                  width: isRect ? 6 * p.scale : 4 * p.scale,
                  height: isRect ? 3 * p.scale : 4 * p.scale,
                  borderRadius: isRect ? 1 : 999,
                }}
                initial={{ x: 0, y: 0, scale: 0, opacity: PARTICLE_OPACITIES[p.id % PARTICLE_OPACITIES.length], rotate: 0 }}
                animate={{
                  x: tx,
                  y: ty,
                  scale: [0, 1.3, 0.5],
                  opacity: [PARTICLE_OPACITIES[p.id % PARTICLE_OPACITIES.length], PARTICLE_OPACITIES[p.id % PARTICLE_OPACITIES.length], 0],
                  rotate: Math.random() * 360,
                }}
                transition={{
                  duration: 0.65,
                  ease: [0.22, 1, 0.36, 1],
                  delay: p.id * 0.012,
                }}
              />
            );
          })}
      </AnimatePresence>

      {/* Glow pulse */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            className="absolute rounded-full bg-primary"
            initial={{ width: 8, height: 8, opacity: 0.5 }}
            animate={{ width: 40, height: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>

      {/* Check icon with bounce */}
      <motion.div
        animate={
          isCompleted
            ? { scale: [1, 1.6, 0.9, 1.15, 1], rotate: [0, 15, -8, 4, 0] }
            : { scale: 1, rotate: 0 }
        }
        transition={
          isCompleted
            ? {
                duration: 0.55,
                ease: [0.22, 1, 0.36, 1],
                times: [0, 0.2, 0.45, 0.7, 1],
              }
            : { duration: 0.15 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className={`size-5 transition-colors duration-200 ${isCompleted ? "text-primary" : ""}`}
          style={{ strokeWidth: isCompleted ? 3 : 2 }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Confetti Clean (no floating text) ─────────────────────

function ConfettiCleanAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  const particles = useRef(createConfettiParticles(16));

  return (
    <div className="relative flex items-center justify-center">
      {/* Confetti particles */}
      <AnimatePresence>
        {isCompleted &&
          particles.current.map((p) => {
            const rad = (p.angle * Math.PI) / 180;
            const tx = p.x + Math.cos(rad) * p.distance;
            const ty = Math.sin(rad) * p.distance;
            const isRect = p.id % 3 === 0;
            return (
              <motion.div
                key={p.id}
                className="absolute bg-primary"
                style={{
                  width: isRect ? 6 * p.scale : 4 * p.scale,
                  height: isRect ? 3 * p.scale : 4 * p.scale,
                  borderRadius: isRect ? 1 : 999,
                }}
                initial={{ x: 0, y: 0, scale: 0, opacity: PARTICLE_OPACITIES[p.id % PARTICLE_OPACITIES.length], rotate: 0 }}
                animate={{
                  x: tx,
                  y: ty,
                  scale: [0, 1.3, 0.5],
                  opacity: [PARTICLE_OPACITIES[p.id % PARTICLE_OPACITIES.length], PARTICLE_OPACITIES[p.id % PARTICLE_OPACITIES.length], 0],
                  rotate: Math.random() * 360,
                }}
                transition={{
                  duration: 0.65,
                  ease: [0.22, 1, 0.36, 1],
                  delay: p.id * 0.012,
                }}
              />
            );
          })}
      </AnimatePresence>

      {/* Glow pulse */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            className="absolute rounded-full bg-primary"
            initial={{ width: 8, height: 8, opacity: 0.5 }}
            animate={{ width: 40, height: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>

      {/* Check icon with bounce */}
      <motion.div
        animate={
          isCompleted
            ? { scale: [1, 1.6, 0.9, 1.15, 1], rotate: [0, 15, -8, 4, 0] }
            : { scale: 1, rotate: 0 }
        }
        transition={
          isCompleted
            ? {
                duration: 0.55,
                ease: [0.22, 1, 0.36, 1],
                times: [0, 0.2, 0.45, 0.7, 1],
              }
            : { duration: 0.15 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className={`size-5 transition-colors duration-200 ${isCompleted ? "text-primary" : ""}`}
          style={{ strokeWidth: isCompleted ? 3 : 2 }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Twitter Ring (ring burst + evenly-spaced dot ring, like Twitter heart) ─

function createRingParticles(count: number): Particle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 0,
    y: 0,
    scale: i % 2 === 0 ? 0.6 : 0.4,
    angle: (360 / count) * i,
    distance: i % 2 === 0 ? 24 : 18,
  }));
}

function TwitterRingAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  const particles = useRef(createRingParticles(14));

  return (
    <div className="relative flex items-center justify-center">
      {/* Expanding ring that appears then vanishes */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            className="absolute rounded-full"
            style={{ border: "2.5px solid var(--primary)" }}
            initial={{ width: 0, height: 0, opacity: 1 }}
            animate={{ width: 38, height: 38, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          />
        )}
      </AnimatePresence>

      {/* Dot ring — alternating large/small dots in a perfect circle */}
      <AnimatePresence>
        {isCompleted &&
          particles.current.map((p) => {
            const rad = (p.angle * Math.PI) / 180;
            const tx = Math.cos(rad) * p.distance;
            const ty = Math.sin(rad) * p.distance;
            const dotSize = 6 * p.scale;
            return (
              <motion.div
                key={p.id}
                className="absolute rounded-full bg-primary"
                style={{ width: dotSize, height: dotSize }}
                initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                animate={{
                  x: tx,
                  y: ty,
                  scale: [0, 1.4, 0],
                  opacity: [1, 1, 0],
                }}
                transition={{
                  duration: 0.5,
                  ease: [0.22, 1, 0.36, 1],
                  delay: 0.05 + p.id * 0.008,
                }}
              />
            );
          })}
      </AnimatePresence>

      {/* Fill circle behind check — same as queue/dismiss button bg */}
      <motion.div
        className="absolute rounded-full bg-primary"
        animate={
          isCompleted
            ? { scale: [0, 1.1, 1], opacity: [0, 1, 1] }
            : { scale: 0, opacity: 0 }
        }
        transition={
          isCompleted
            ? { duration: 0.25, ease: [0.22, 1, 0.36, 1] }
            : { duration: 0.1 }
        }
        style={{ width: 28, height: 28 }}
      />

      {/* Check icon — same squish-pop as queue/dismiss buttons */}
      <motion.div
        className="relative z-10"
        animate={
          isCompleted
            ? { scale: [1, 0.7, 1.3, 1] }
            : { scale: 1 }
        }
        transition={
          isCompleted
            ? { duration: 0.35, ease: [0.22, 1, 0.36, 1], times: [0, 0.3, 0.65, 1] }
            : { duration: 0.1 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className="size-3.5 transition-colors duration-100"
          style={{
            color: isCompleted ? "var(--primary-foreground)" : "currentColor",
            strokeWidth: isCompleted ? 2.5 : 2,
          }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Twitter Burst (scale punch + staggered particle rays) ──

function createStarburstParticles(count: number): Particle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 0,
    y: 0,
    scale: 0.3 + Math.random() * 0.4,
    angle: (360 / count) * i + (Math.random() - 0.5) * 10,
    distance: 16 + Math.random() * 16,
  }));
}

function TwitterBurstAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  const particles = useRef(createStarburstParticles(8));

  return (
    <div className="relative flex items-center justify-center">
      {/* Rays — short lines radiating outward */}
      <AnimatePresence>
        {isCompleted &&
          particles.current.map((p) => {
            const rad = (p.angle * Math.PI) / 180;
            const startDist = 10;
            const endDist = p.distance;
            return (
              <motion.div
                key={p.id}
                className="absolute bg-primary"
                style={{
                  width: 3,
                  height: 10 * p.scale,
                  borderRadius: 1,
                  transformOrigin: "center center",
                  rotate: `${p.angle + 90}deg`,
                }}
                initial={{
                  x: Math.cos(rad) * startDist,
                  y: Math.sin(rad) * startDist,
                  scale: 0,
                  opacity: 1,
                }}
                animate={{
                  x: Math.cos(rad) * endDist,
                  y: Math.sin(rad) * endDist,
                  scale: [0, 1.2, 0],
                  opacity: [1, 1, 0],
                }}
                transition={{
                  duration: 0.45,
                  ease: [0.22, 1, 0.36, 1],
                  delay: p.id * 0.02,
                }}
              />
            );
          })}
      </AnimatePresence>

      {/* Quick flash ring */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            className="absolute rounded-full bg-primary/25"
            initial={{ width: 8, height: 8, opacity: 0.8 }}
            animate={{ width: 44, height: 44, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>

      {/* Check icon — Twitter-style overshoot bounce */}
      <motion.div
        animate={
          isCompleted
            ? { scale: [1, 0.4, 1.5, 0.85, 1.1, 1], rotate: [0, 0, -12, 6, -2, 0] }
            : { scale: 1, rotate: 0 }
        }
        transition={
          isCompleted
            ? { duration: 0.55, ease: [0.22, 1, 0.36, 1], times: [0, 0.15, 0.35, 0.55, 0.75, 1] }
            : { duration: 0.1 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className={`size-5 transition-colors duration-150 ${isCompleted ? "text-primary" : ""}`}
          style={{ strokeWidth: isCompleted ? 3 : 2 }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Twitter Sparkle (fill + sparkle particles that twinkle) ─

function createSparkleParticles(count: number): Particle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: 0,
    y: 0,
    scale: 0.4 + Math.random() * 0.5,
    angle: (360 / count) * i + (Math.random() - 0.5) * 30,
    distance: 18 + Math.random() * 14,
  }));
}

function TwitterSparkleAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  const particles = useRef(createSparkleParticles(10));

  return (
    <div className="relative flex items-center justify-center">
      {/* Sparkle particles — 4-pointed stars that pop and twinkle */}
      <AnimatePresence>
        {isCompleted &&
          particles.current.map((p) => {
            const rad = (p.angle * Math.PI) / 180;
            const tx = Math.cos(rad) * p.distance;
            const ty = Math.sin(rad) * p.distance;
            const size = 9 * p.scale;
            return (
              <motion.div
                key={p.id}
                className="absolute text-primary"
                style={{
                  width: size,
                  height: size,
                  /* 4-pointed star via clip-path */
                  clipPath: "polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)",
                  backgroundColor: "currentColor",
                }}
                initial={{ x: 0, y: 0, scale: 0, opacity: 1, rotate: 0 }}
                animate={{
                  x: tx,
                  y: ty,
                  scale: [0, 1.5, 0.8, 1.2, 0],
                  opacity: [1, 1, 1, 0.8, 0],
                  rotate: [0, 45, 90],
                }}
                transition={{
                  duration: 0.6,
                  ease: [0.22, 1, 0.36, 1],
                  delay: p.id * 0.025,
                }}
              />
            );
          })}
      </AnimatePresence>

      {/* Expanding glow */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            className="absolute rounded-full bg-primary/20"
            initial={{ width: 6, height: 6, opacity: 0.6 }}
            animate={{ width: 36, height: 36, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>

      {/* Fill circle */}
      <motion.div
        className="absolute rounded-full bg-primary"
        animate={
          isCompleted
            ? { scale: [0, 1.15, 1], opacity: [0, 1, 1] }
            : { scale: 0, opacity: 0 }
        }
        transition={
          isCompleted
            ? { duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }
            : { duration: 0.1 }
        }
        style={{ width: 28, height: 28 }}
      />

      {/* Check icon — delayed pop-in after fill */}
      <motion.div
        className="relative z-10"
        animate={
          isCompleted
            ? { opacity: [0, 0, 1], scale: [0.5, 0.5, 1.2, 0.95, 1] }
            : { opacity: 1, scale: 1 }
        }
        transition={
          isCompleted
            ? { duration: 0.5, ease: [0.34, 1.56, 0.64, 1], times: [0, 0.3, 0.55, 0.75, 1] }
            : { duration: 0.1 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className="size-5 transition-colors duration-100"
          style={{
            color: isCompleted ? "var(--primary-foreground)" : "currentColor",
            strokeWidth: isCompleted ? 2.5 : 2,
          }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Ink (color floods outward, then check appears) ────────

function InkAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  return (
    <div className="relative flex items-center justify-center">
      {/* Ink fill — radial scale from center */}
      <motion.div
        className="absolute rounded-full bg-primary"
        animate={
          isCompleted
            ? { scale: [0, 1.1, 1], opacity: [0, 1, 1] }
            : { scale: 0, opacity: 0 }
        }
        transition={
          isCompleted
            ? { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
            : { duration: 0.1 }
        }
        style={{ width: 32, height: 32 }}
      />

      {/* Check icon — fades in after fill, with a subtle pop */}
      <motion.div
        className="relative z-10"
        animate={
          isCompleted
            ? { opacity: [0, 0, 1], scale: [0.5, 0.5, 1.15, 1] }
            : { opacity: 1, scale: 1 }
        }
        transition={
          isCompleted
            ? {
                duration: 0.45,
                ease: [0.34, 1.56, 0.64, 1],
                times: [0, 0.35, 0.7, 1],
              }
            : { duration: 0.1 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className="size-5 transition-colors duration-100"
          style={{
            color: isCompleted ? "var(--primary-foreground)" : "currentColor",
            strokeWidth: isCompleted ? 2.5 : 2,
          }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Press (neumorphic depth press, then release) ──────────

function PressAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  return (
    <div className="relative flex items-center justify-center">
      {/* Shadow container — inverts from raised to pressed to raised-completed */}
      <motion.div
        className="absolute rounded-lg"
        style={{ width: 30, height: 30 }}
        animate={
          isCompleted
            ? {
                boxShadow: [
                  "0 2px 4px rgba(0,0,0,0.15)",
                  "inset 0 2px 6px rgba(0,0,0,0.25)",
                  "0 1px 3px rgba(0,0,0,0.1)",
                ],
                backgroundColor: [
                  "var(--muted)",
                  "var(--muted)",
                  "var(--primary)",
                ],
              }
            : {
                boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
                backgroundColor: "var(--muted)",
              }
        }
        transition={
          isCompleted
            ? { duration: 0.4, times: [0, 0.35, 1], ease: [0.22, 1, 0.36, 1] }
            : { duration: 0.1 }
        }
      />

      {/* Check icon — presses down, then bounces up */}
      <motion.div
        className="relative z-10"
        animate={
          isCompleted
            ? {
                y: [0, 3, -2, 0],
                scale: [1, 0.8, 1.1, 1],
              }
            : { y: 0, scale: 1 }
        }
        transition={
          isCompleted
            ? {
                duration: 0.45,
                ease: [0.22, 1, 0.36, 1],
                times: [0, 0.3, 0.65, 1],
              }
            : { duration: 0.1 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className="size-5 transition-colors duration-150"
          style={{
            color: isCompleted ? "var(--primary-foreground)" : "currentColor",
            strokeWidth: isCompleted ? 2.5 : 2,
          }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Swing (pendulum swing-in with damping) ────────────────

function SwingAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  return (
    <div className="relative flex items-center justify-center">
      {/* Subtle landing dot */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            className="absolute rounded-full bg-primary"
            style={{ width: 4, height: 4, bottom: -4 }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 1.5, 1], opacity: [0, 0.6, 0.4] }}
            transition={{ duration: 0.3, delay: 0.35, ease: "easeOut" }}
          />
        )}
      </AnimatePresence>

      {/* Check icon — swings in like a pendulum (origin at top) */}
      <motion.div
        style={{ originY: 0 }}
        animate={
          isCompleted
            ? { rotate: [-60, 12, -5, 2, 0], opacity: [0, 1, 1, 1, 1] }
            : { rotate: 0, opacity: 1 }
        }
        transition={
          isCompleted
            ? {
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1],
                times: [0, 0.35, 0.55, 0.75, 1],
              }
            : { duration: 0.1 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className={`size-5 transition-colors duration-200 ${isCompleted ? "text-primary" : ""}`}
          style={{ strokeWidth: isCompleted ? 3 : 2 }}
        />
      </motion.div>
    </div>
  );
}

// ─── Variant: Wipe (conic clock-wipe reveal) ────────────────────────

function WipeAnimation({
  isCompleted,
  onAnimationComplete,
}: {
  isCompleted: boolean;
  onAnimationComplete: () => void;
}) {
  return (
    <div className="relative flex items-center justify-center">
      {/* Conic sweep fill — a circle revealed by a rotating mask */}
      <motion.div
        className="absolute rounded-full bg-primary"
        style={{
          width: 30,
          height: 30,
          // Conic gradient mask: the "angle" portion is revealed
          maskImage: "conic-gradient(#000 var(--wipe-angle), transparent 0)",
          WebkitMaskImage: "conic-gradient(#000 var(--wipe-angle), transparent 0)",
        }}
        animate={
          isCompleted
            ? { "--wipe-angle": ["0deg", "380deg", "360deg"] }
            : { "--wipe-angle": "0deg" }
        }
        transition={
          isCompleted
            ? { duration: 0.45, ease: [0.22, 1, 0.36, 1], times: [0, 0.85, 1] }
            : { duration: 0.1 }
        }
      />

      {/* Check icon — pops in after the wipe completes */}
      <motion.div
        className="relative z-10"
        animate={
          isCompleted
            ? { scale: [0.6, 0.6, 1.15, 1], opacity: [0, 0, 1, 1] }
            : { scale: 1, opacity: 1 }
        }
        transition={
          isCompleted
            ? {
                duration: 0.5,
                ease: [0.34, 1.56, 0.64, 1],
                times: [0, 0.5, 0.8, 1],
              }
            : { duration: 0.1 }
        }
        onAnimationComplete={() => {
          if (isCompleted) onAnimationComplete();
        }}
      >
        <Check
          className="size-5 transition-colors duration-100"
          style={{
            color: isCompleted ? "var(--primary-foreground)" : "currentColor",
            strokeWidth: isCompleted ? 2.5 : 2,
          }}
        />
      </motion.div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────────────

export function CompletionAnimationButton({
  variant,
  onComplete,
  label,
  size = "md",
}: CompletionAnimationProps) {
  const [isCompleted, setIsCompleted] = useState(false);
  const [showDone, setShowDone] = useState(false);

  const handleClick = useCallback(() => {
    if (isCompleted) return;
    setIsCompleted(true);
  }, [isCompleted]);

  const handleAnimationComplete = useCallback(() => {
    setShowDone(true);
    onComplete?.();
  }, [onComplete]);

  const handleReset = useCallback(() => {
    setIsCompleted(false);
    setShowDone(false);
  }, []);

  const sizeClass = size === "sm" ? "h-8 w-8" : "h-10 w-10";

  return (
    <div className="flex flex-col items-center gap-3">
      <Button
        variant="ghost"
        className={`${sizeClass} rounded-full relative overflow-visible cursor-pointer ${
          isCompleted ? "" : "hover:bg-muted"
        }`}
        onClick={handleClick}
      >
        {variant === "burst" && (
          <BurstAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "pop" && (
          <PopAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "ripple" && (
          <RippleAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "snap" && (
          <SnapAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "confetti" && (
          <ConfettiAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "confetti-clean" && (
          <ConfettiCleanAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "twitter-ring" && (
          <TwitterRingAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "twitter-burst" && (
          <TwitterBurstAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "twitter-sparkle" && (
          <TwitterSparkleAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "ink" && (
          <InkAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "press" && (
          <PressAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "swing" && (
          <SwingAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
        {variant === "wipe" && (
          <WipeAnimation
            isCompleted={isCompleted}
            onAnimationComplete={handleAnimationComplete}
          />
        )}
      </Button>

      {label && (
        <span className="text-xs text-muted-foreground">{label}</span>
      )}

      <AnimatePresence>
        {showDone && (
          <motion.button
            type="button"
            className="text-[11px] text-muted-foreground hover:text-foreground cursor-pointer"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            onClick={handleReset}
          >
            Reset
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Inline check button for embedding in a feed row ────────────────

interface InlineCompletionCheckProps {
  variant: CompletionVariant;
  onAnimationDone: () => void;
  /** External trigger (e.g. keyboard shortcut) — set to true to start animation */
  triggerAnimation?: boolean;
}

export function InlineCompletionCheck({
  variant,
  onAnimationDone,
  triggerAnimation,
}: InlineCompletionCheckProps) {
  const [isCompleted, setIsCompleted] = useState(false);
  const firedRef = useRef(false);

  const fire = useCallback(() => {
    if (isCompleted) return;
    setIsCompleted(true);
    firedRef.current = false;
    // Let the sparkles/particles play out before starting the row exit
    setTimeout(() => {
      if (!firedRef.current) {
        firedRef.current = true;
        onAnimationDone();
      }
    }, 450);
  }, [isCompleted, onAnimationDone]);

  // Allow external trigger via prop (e.g. keyboard shortcut "c")
  const prevTrigger = useRef(false);
  if (triggerAnimation && !prevTrigger.current) {
    prevTrigger.current = true;
    // Schedule to avoid setState during render
    setTimeout(fire, 0);
  }

  const handleClick = useCallback(() => {
    fire();
  }, [fire]);

  const handleAnimationComplete = useCallback(() => {
    // no-op — row collapse already triggered midway via timeout
  }, []);

  const ANIMATION_MAP = {
    burst: BurstAnimation,
    pop: PopAnimation,
    ripple: RippleAnimation,
    snap: SnapAnimation,
    confetti: ConfettiAnimation,
    "confetti-clean": ConfettiCleanAnimation,
    "twitter-ring": TwitterRingAnimation,
    "twitter-burst": TwitterBurstAnimation,
    "twitter-sparkle": TwitterSparkleAnimation,
    ink: InkAnimation,
    press: PressAnimation,
    swing: SwingAnimation,
    wipe: WipeAnimation,
  } as const;
  const AnimationComponent = ANIMATION_MAP[variant];

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      className="bg-muted active:scale-95 relative overflow-visible cursor-pointer"
      tabIndex={-1}
      onClick={handleClick}
    >
      <div>
        <AnimationComponent
          isCompleted={isCompleted}
          onAnimationComplete={handleAnimationComplete}
        />
      </div>
    </Button>
  );
}

// ─── Inline animated action button (queue, backlog, dismiss) ─────────

export type ActionAnimationType = "queue" | "backlog" | "dismiss";

interface InlineActionButtonProps {
  type: ActionAnimationType;
  icon: ReactNode;
  onAnimationDone: () => void;
  triggerAnimation?: boolean;
  /** "sm" for feed rows (default), "lg" for full-screen swipe/queue modes */
  size?: "sm" | "lg";
}

export function InlineActionButton({
  type,
  icon,
  onAnimationDone,
  triggerAnimation,
  size = "sm",
}: InlineActionButtonProps) {
  const [phase, setPhase] = useState<"idle" | "active" | "resetting">("idle");
  const firedRef = useRef(false);

  const fire = useCallback(() => {
    if (phase !== "idle") return;
    setPhase("active");
    firedRef.current = false;
    setTimeout(() => {
      if (!firedRef.current) {
        firedRef.current = true;
        onAnimationDone();
        // Fade out, then fully reset
        setPhase("resetting");
        setTimeout(() => {
          setPhase("idle");
          firedRef.current = false;
        }, 250);
      }
    }, 200);
  }, [phase, onAnimationDone]);

  const isActive = phase === "active";
  const isResetting = phase === "resetting";

  const prevTrigger = useRef(false);
  if (triggerAnimation && !prevTrigger.current) {
    prevTrigger.current = true;
    setTimeout(fire, 0);
  }

  type MotionAnim = { animate: Record<string, unknown>; transition: Record<string, unknown> };
  const animations: Record<ActionAnimationType, { icon: MotionAnim; bg: MotionAnim }> = {
    queue: {
      icon: {
        animate: { scale: [1, 0.7, 1.3, 1] },
        transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1], times: [0, 0.3, 0.65, 1] },
      },
      bg: {
        animate: { scale: [0, 1.1, 1], opacity: [0, 1, 1] },
        transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
      },
    },
    dismiss: {
      icon: {
        animate: { scale: [1, 0.7, 1.15, 1] },
        transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1], times: [0, 0.3, 0.65, 1] },
      },
      bg: {
        animate: { scale: [0, 1.1, 1], opacity: [0, 0.8, 0.8] },
        transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
      },
    },
    backlog: {
      icon: {
        animate: { y: [0, 3, -2, 0], scale: [1, 0.85, 1.1, 1] },
        transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1], times: [0, 0.3, 0.65, 1] },
      },
      bg: {
        animate: { scale: [0, 1.1, 1], opacity: [0, 1, 1] },
        transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] },
      },
    },
  };

  const anim = animations[type];

  const BG_COLORS: Record<ActionAnimationType, string> = {
    queue: "var(--color-blue-500)",
    backlog: "var(--color-blue-500)",
    dismiss: "var(--color-red-500)",
  };

  const FG_COLORS: Record<ActionAnimationType, string> = {
    queue: "#fff",
    backlog: "#fff",
    dismiss: "#fff",
  };

  const BORDER_CLASSES: Record<ActionAnimationType, string> = {
    queue: "border-blue-500 text-blue-500 hover:bg-blue-500/10",
    backlog: "border-blue-500 text-blue-500 hover:bg-blue-500/10",
    dismiss: "border-destructive text-destructive hover:bg-destructive/10",
  };

  const isLg = size === "lg";
  const bgSize = isLg ? 56 : 28;
  const iconSizeClass = isLg ? "size-6 [&>svg]:size-6" : "size-3.5 [&>svg]:size-3.5";
  const btnClass = isLg
    ? `rounded-full size-14 active:scale-95 relative overflow-visible cursor-pointer ${BORDER_CLASSES[type]}`
    : "bg-muted active:scale-95 relative overflow-visible cursor-pointer";

  return (
    <Button
      variant={isLg ? "outline" : "ghost"}
      size={isLg ? "lg" : "icon-sm"}
      className={btnClass}
      style={isLg && isActive ? { borderColor: BG_COLORS[type] } : undefined}
      tabIndex={-1}
      onClick={fire}
    >
      <div className="relative flex items-center justify-center">
        {/* Background fill — fades out on reset instead of scaling back */}
        <motion.div
          className="absolute rounded-full"
          style={{ width: bgSize, height: bgSize, backgroundColor: BG_COLORS[type] }}
          animate={
            (isActive
              ? anim.bg.animate
              : isResetting
                ? { scale: 1, opacity: 0 }
                : { scale: 0, opacity: 0 }) as React.ComponentProps<typeof motion.div>["animate"]
          }
          transition={
            (isActive
              ? anim.bg.transition
              : isResetting
                ? { duration: 0.2, ease: "easeOut" }
                : { duration: 0 }) as React.ComponentProps<typeof motion.div>["transition"]
          }
        />
        {/* Icon */}
        <motion.div
          className="relative z-10"
          animate={(isActive ? anim.icon.animate : {}) as React.ComponentProps<typeof motion.div>["animate"]}
          transition={(isActive ? anim.icon.transition : { duration: 0 }) as React.ComponentProps<typeof motion.div>["transition"]}
        >
          <div
            className={`${iconSizeClass} transition-colors duration-100`}
            style={{ color: isActive ? FG_COLORS[type] : "currentColor" }}
          >
            {icon}
          </div>
        </motion.div>
      </div>
    </Button>
  );
}

// ─── Full-screen completion check (larger, for swipe/queue modes) ─────

interface FullScreenCompletionCheckProps {
  onAnimationDone: () => void;
}

export function FullScreenCompletionCheck({
  onAnimationDone,
}: FullScreenCompletionCheckProps) {
  const [phase, setPhase] = useState<"idle" | "active" | "resetting">("idle");
  const firedRef = useRef(false);
  const particles = useRef(createRingParticles(14));

  const fire = useCallback(() => {
    if (phase !== "idle") return;
    setPhase("active");
    firedRef.current = false;
    setTimeout(() => {
      if (!firedRef.current) {
        firedRef.current = true;
        onAnimationDone();
        // Fade out, then fully reset
        setPhase("resetting");
        setTimeout(() => {
          setPhase("idle");
          firedRef.current = false;
        }, 250);
      }
    }, 450);
  }, [phase, onAnimationDone]);

  const isActive = phase === "active";
  const isResetting = phase === "resetting";

  return (
    <Button
      variant="outline"
      size="lg"
      className="rounded-full size-14 border-green-500 text-green-500 hover:bg-green-500/10 active:scale-95 relative overflow-visible cursor-pointer"
      style={isActive ? { borderColor: "var(--color-green-500)" } : undefined}
      tabIndex={-1}
      onClick={fire}
    >
      <div className="relative flex items-center justify-center">
        {/* Expanding ring */}
        <AnimatePresence>
          {isActive && (
            <motion.div
              className="absolute rounded-full"
              style={{ border: "2.5px solid var(--color-green-500)" }}
              initial={{ width: 0, height: 0, opacity: 1 }}
              animate={{ width: 72, height: 72, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            />
          )}
        </AnimatePresence>

        {/* Dot ring particles */}
        <AnimatePresence>
          {isActive &&
            particles.current.map((p) => {
              const rad = (p.angle * Math.PI) / 180;
              const dist = p.distance * 2.1;
              const tx = Math.cos(rad) * dist;
              const ty = Math.sin(rad) * dist;
              const dotSize = 10 * p.scale;
              return (
                <motion.div
                  key={p.id}
                  className="absolute rounded-full"
                  style={{ width: dotSize, height: dotSize, backgroundColor: "var(--color-green-500)" }}
                  initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                  animate={{
                    x: tx,
                    y: ty,
                    scale: [0, 1.4, 0],
                    opacity: [1, 1, 0],
                  }}
                  transition={{
                    duration: 0.5,
                    ease: [0.22, 1, 0.36, 1],
                    delay: 0.05 + p.id * 0.008,
                  }}
                />
              );
            })}
        </AnimatePresence>

        {/* Fill circle — fades out on reset instead of scaling back */}
        <motion.div
          className="absolute rounded-full"
          animate={
            isActive
              ? { scale: [0, 1.1, 1], opacity: [0, 1, 1] }
              : isResetting
                ? { scale: 1, opacity: 0 }
                : { scale: 0, opacity: 0 }
          }
          transition={
            isActive
              ? { duration: 0.25, ease: [0.22, 1, 0.36, 1] }
              : isResetting
                ? { duration: 0.2, ease: "easeOut" }
                : { duration: 0 }
          }
          style={{ width: 56, height: 56, backgroundColor: "var(--color-green-500)" }}
        />

        {/* Check icon */}
        <motion.div
          className="relative z-10"
          animate={
            isActive
              ? { scale: [1, 0.7, 1.3, 1] }
              : { scale: 1 }
          }
          transition={
            isActive
              ? { duration: 0.35, ease: [0.22, 1, 0.36, 1], times: [0, 0.3, 0.65, 1] }
              : { duration: 0 }
          }
        >
          <Check
            className="size-6 transition-colors duration-100"
            style={{
              color: isActive ? "var(--primary-foreground)" : "currentColor",
              strokeWidth: isActive ? 2.5 : 2,
            }}
          />
        </motion.div>
      </div>
    </Button>
  );
}

// ─── Row-level completion animation (wraps a feed item row) ─────────

interface CompletionRowWrapperProps {
  variant: CompletionVariant;
  children: React.ReactNode;
  isCompleted: boolean;
}

export function CompletionRowWrapper({
  variant,
  children,
  isCompleted,
}: CompletionRowWrapperProps) {
  if (variant === "ripple") {
    return (
      <motion.div
        animate={
          isCompleted
            ? { height: 0, opacity: 0, marginBottom: 0 }
            : { height: "auto", opacity: 1 }
        }
        transition={{
          height: { duration: 0.35, delay: 0.15, ease: [0.22, 1, 0.36, 1] },
          opacity: { duration: 0.2, delay: 0.1 },
        }}
        className="overflow-hidden"
      >
        {children}
      </motion.div>
    );
  }

  return (
    <motion.div
      animate={
        isCompleted
          ? { x: 80, opacity: 0, height: 0, marginBottom: 0 }
          : { x: 0, opacity: 1, height: "auto" }
      }
      transition={{
        x: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
        opacity: { duration: 0.25 },
        height: { duration: 0.3, delay: 0.15, ease: [0.22, 1, 0.36, 1] },
      }}
      className="overflow-hidden"
    >
      {children}
    </motion.div>
  );
}
